import React, { useState } from 'react'
import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { GradientButton, WhiteButton } from '../Button'

interface Props {
  open: boolean
  onClose: () => void
  onConfirm(): void
  confirmText: string // The string the user must type (e.g., "CANCEL")
}

const useStyles = makeStyles((theme) => ({
  actions: {
    padding: theme.spacing(2, 3),
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
  description: {
    marginBottom: theme.spacing(3),
    // Ensures long Hungarian words wrap correctly
    wordBreak: 'break-word',
    lineHeight: 1.5,
  },
  dialogPaper: {
    padding: theme.spacing(2),
    borderRadius: '1rem',
    width: '100%',
  },
  monospace: {
    fontFamily: 'monospace',
    fontWeight: 'bold',
    marginLeft: theme.spacing(1),
  },
  textField: {
    marginTop: theme.spacing(2),
  },
}))

const CancelSubscription: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  confirmText,
}) => {
  const classes = useStyles()
  const { t } = useTranslation('subscriptionCard')
  const [userInput, setUserInput] = useState('')

  // Logic fix: The previous code had "if (!isConfirmed) { onConfirm() }"
  // which would only fire if the text DID NOT match. Fixed below:
  const isConfirmed = userInput === confirmText

  const handleConfirm = () => {
    if (isConfirmed) {
      onConfirm()
      setUserInput('')
      onClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm" // Provides a comfortable width for translation strings
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogTitle>
        <Typography variant="h6">{t('confirmCancelTitle')}</Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" className={classes.description}>
          {t('confirmCancelDescription')}
          <span className={classes.monospace}>{confirmText}</span>
        </Typography>

        <TextField
          className={classes.textField}
          fullWidth
          value={userInput}
          placeholder={confirmText}
          onChange={(e) => setUserInput(e.target.value)}
          variant="outlined"
          autoFocus
        />
      </DialogContent>

      <DialogActions className={classes.actions}>
        <WhiteButton onClick={onClose}>{t('closeCancelModal')}</WhiteButton>
        <GradientButton onClick={handleConfirm} disabled={!isConfirmed}>
          {t('confirmCancelButton')}
        </GradientButton>
      </DialogActions>
    </Dialog>
  )
}

CancelSubscription.displayName = 'CancelSubscription'
export { CancelSubscription }
